import React, { useState, useEffect } from 'react';
import { Radar } from 'react-chartjs-2';
import api from '../../utils/api';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { useNavigate } from 'react-router-dom';

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
);

const BumdesVECISummary = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchResult = async () => {
      setIsLoading(true);
      try {
        const response = await api.getMeBumdesVECIResult();
        if (response.status === 'success') {
          setData(response.data);
        }
      } catch (error) {
        console.error('Fetch Result Error:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchResult();
  }, []);

  if (isLoading) {
    return (
      <div className='d-flex justify-content-center py-5'>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const radarData = {
    labels: ['OP', 'AD', 'MP'],
    datasets: [
      {
        label: 'VECI Score',
        data: [
          Number(data.dimensions.OP.score) || 0,
          Number(data.dimensions.AD.score) || 0,
          Number(data.dimensions.MP.score) || 0,
        ],
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 2,
        pointBackgroundColor: 'rgba(54, 162, 235, 1)',
      },
    ],
  };

  const radarOptions = {
    scales: {
      r: {
        angleLines: { display: true },
        suggestedMin: 0,
        suggestedMax: 5,
        ticks: { display: false },
      },
    },
    plugins: {
      legend: { display: false },
    },
  };

  const categories = [
    {
      min: 40,
      max: 50,
      label: 'Daya saing sangat bagus',
      color: '#146c43',
      bg: '#d1e7dd',
      text: '#0a3622',
      implication:
        'BUMDes kompetitif tinggi. Fokus program: scale-up dan replikasi praktik terbaik',
    },
    {
      min: 30,
      max: 40,
      label: 'Daya saing bagus',
      color: '#a3cfbb',
      bg: '#f0f9f4',
      text: '#146c43',
      implication:
        'BUMDes sudah kompetitif. Fokus program: penguatan area yang masih lemah',
    },
    {
      min: 20,
      max: 30,
      label: 'Cukup',
      color: '#ffc107',
      bg: '#fff9e6',
      text: '#664d03',
      implication:
        'BUMDes berkembang. Fokus program: peningkatan kapasitas dan sistem operasional',
    },
    {
      min: 10,
      max: 20,
      label: 'Daya saing kurang',
      color: '#fd7e14',
      bg: '#fff5eb',
      text: '#7e3f0a',
      implication:
        'BUMDes tahap awal. Fokus program: pembangunan pondasi bisnis',
    },
    {
      min: 2,
      max: 10,
      label: 'Tidak ada daya saing',
      color: '#dc3545',
      bg: '#f8d7da',
      text: '#842029',
      implication:
        'BUMDes perlu intervensi mendasar. Analisis mendalam diperlukan sebelum program dilanjutkan',
    },
  ];

  const currentScore = Number(data.totalScore);
  const currentCat =
    categories.find((c) => currentScore >= c.min) ||
    categories[categories.length - 1];
  const nextCat = categories[categories.indexOf(currentCat) - 1];
  const pointsToNext = nextCat ? (nextCat.min - currentScore).toFixed(1) : 0;

  const statusConfig = {
    APPROVED: {
      color: 'success',
      icon: 'bi-check-circle-fill',
      label: 'Telah Disetujui',
    },
    REVISION_REQUESTED: {
      color: 'danger',
      icon: 'bi-exclamation-triangle-fill',
      label: 'Perlu Revisi',
    },
    SUBMITTED: {
      color: 'warning',
      icon: 'bi-clock-fill',
      label: 'Menunggu Review',
    },
    DRAFT: {
      color: 'secondary',
      icon: 'bi-pencil-fill',
      label: 'Draft',
    },
  }[data.status] || {
    color: 'warning',
    icon: 'bi-dot',
    label: data.status,
  };

  return (
    <div className='mt-5'>
      <h4 className='fw-bold text-dark mb-4'>
        <i className='bi bi-bar-chart-fill text-primary me-2'></i>Ringkasan
        Hasil VECI
      </h4>

      {/* Status Approval Section */}
      <div
        className={`card border-0 shadow-sm rounded-4 mb-4 overflow-hidden border-top border-${statusConfig.color} border-4`}
      >
        <div className='card-body p-4'>
          <div className='d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3'>
            <div>
              <div className='text-uppercase extra-small fw-bold text-muted mb-2'>
                Status Approval
              </div>
              <div
                className={`d-flex align-items-center text-${statusConfig.color} fw-bold text-uppercase`}
              >
                <i className={`bi ${statusConfig.icon} me-2`}></i>{' '}
                {statusConfig.label}
              </div>
            </div>
            {data.status === 'REVISION_REQUESTED' && (
              <button
                className='btn btn-outline-dark rounded-pill px-4 py-2 border-2 small fw-bold shadow-sm'
                onClick={() => navigate('/bumdes/veci')}
              >
                <i className='bi bi-pencil-square me-2'></i> Perbaiki Survei
              </button>
            )}
          </div>
          {data.reviewNotes && (
            <div
              className={`bg-${statusConfig.color} bg-opacity-10 p-4 rounded-4 border border-${statusConfig.color} border-opacity-25 mt-4`}
            >
              <div className={`small fw-bold text-${statusConfig.color} mb-2`}>
                Catatan Review:
              </div>
              <div
                className='text-dark small mb-0 fw-medium'
                style={{ lineHeight: '1.6' }}
              >
                {data.reviewNotes}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className='row g-4'>
        {/* Top Score Summary */}
        <div className='col-12 col-xl-6'>
          <div className='card border-0 shadow-sm rounded-4 h-100 overflow-hidden'>
            <div className='card-body p-4 p-md-5 text-center d-flex flex-column justify-content-center'>
              <div className='text-uppercase extra-small fw-bold text-muted mb-2 letter-spacing-1'>
                Skor VECI Total — {data.phase}
              </div>
              <h1 className='display-1 fw-bold text-dark mb-0'>
                {data.totalScore}{' '}
                <small className='fs-3 text-muted fw-light'>
                  / {data.maxScore}
                </small>
              </h1>

              <div className='d-flex justify-content-center gap-3 mt-4'>
                <div
                  className='px-4 py-2 rounded-pill fw-bold shadow-sm d-flex align-items-center'
                  style={{
                    backgroundColor: currentCat.bg,
                    color: currentCat.text,
                    border: `1px solid ${currentCat.color}40`,
                    fontSize: '0.85rem',
                  }}
                >
                  <i className='bi bi-award-fill me-2'></i>
                  {currentCat.label}
                </div>
              </div>

              <div className='row g-4 mt-5 pt-3 border-top justify-content-center'>
                {Object.entries(data.dimensions).map(([key, dim]) => (
                  <div className='col-4 text-center' key={key}>
                    <div className='extra-small fw-bold text-muted text-uppercase mb-1'>
                      {key}
                    </div>
                    <div className='h4 fw-bold mb-0 text-dark'>
                      {(Number(dim.score) || 0).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Radar Chart */}
        <div className='col-12 col-xl-6'>
          <div className='card border-0 shadow-sm rounded-4 h-100'>
            <div className='card-body p-4 d-flex flex-column'>
              <div className='text-uppercase extra-small fw-bold text-muted mb-4'>
                Grafik Radar Dimensi
              </div>
              <div
                className='flex-grow-1 d-flex align-items-center justify-content-center'
                style={{ minHeight: '250px' }}
              >
                <Radar data={radarData} options={radarOptions} />
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Analysis Category Card */}
      <div className='card border-0 shadow-sm rounded-4 mt-4 overflow-hidden'>
        <div className='card-body p-4'>
          <div className='d-flex align-items-center justify-content-between mb-4'>
            <div className='text-uppercase extra-small fw-bold text-muted letter-spacing-1'>
              Matriks Kategori Daya Saing
            </div>
            {nextCat && (
              <div className='badge bg-success bg-opacity-10 text-success px-3 py-2 rounded-pill border border-success border-opacity-25 extra-small'>
                <i className='bi bi-arrow-up-circle me-1'></i>
                Butuh {pointsToNext} poin lagi untuk level berikutnya
              </div>
            )}
          </div>

          <div className='table-responsive border rounded-4 bg-white shadow-xs'>
            <table className='table table-sm table-borderless mb-0 align-middle'>
              <thead className='bg-dark text-white extra-small text-uppercase'>
                <tr>
                  <th className='ps-3 py-3'>Skor VECI</th>
                  <th className='py-3'>Kategori</th>
                  <th className='py-3 pe-3'>Implikasi Program BCF</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((cat, i) => {
                  const isCurrent =
                    currentScore >= cat.min &&
                    (i === 0
                      ? currentScore <= cat.max
                      : currentScore < categories[i - 1].min);
                  return (
                    <tr
                      key={i}
                      className={
                        isCurrent ? 'z-1 position-relative shadow-sm' : ''
                      }
                      style={{
                        backgroundColor: isCurrent ? cat.bg : 'transparent',
                        outline: isCurrent
                          ? `2px solid ${cat.color}`
                          : 'none',
                        outlineOffset: '-2px',
                        borderLeft: isCurrent
                          ? `8px solid ${cat.color}`
                          : 'none',
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <td
                        className='ps-3 py-3 fw-bold small'
                        style={{
                          backgroundColor:
                            i === 0
                              ? '#146c43'
                              : i === 1
                                ? '#a3cfbb'
                                : i === 2
                                  ? '#ffc107'
                                  : i === 3
                                    ? '#fd7e14'
                                    : '#dc3545',
                          color: i === 1 || i === 2 ? '#000' : '#fff',
                          width: '120px',
                        }}
                      >
                        {cat.min} — {cat.max}
                      </td>
                      <td
                        className='py-3 small fw-bold px-3'
                        style={{ width: '220px' }}
                      >
                        <div className='d-flex align-items-center'>
                          {cat.label}
                          {isCurrent && (
                            <span
                              className='ms-2 badge rounded-pill px-2 py-1'
                              style={{
                                backgroundColor: cat.color,
                                color: i === 1 || i === 2 ? '#000' : '#fff',
                                fontSize: '9px',
                                letterSpacing: '0.5px',
                              }}
                            >
                              SEKARANG
                            </span>
                          )}
                        </div>
                        {isCurrent && (
                          <div
                            className='extra-small text-uppercase fw-bold mt-1 d-flex align-items-center'
                            style={{ color: cat.color, opacity: 0.9 }}
                          >
                            <i className='bi bi-geo-alt-fill me-1'></i>
                            Posisi BUMDes Saat Ini
                          </div>
                        )}
                      </td>
                      <td
                        className={`py-3 extra-small pe-3 fw-medium ${isCurrent ? 'text-dark' : 'text-muted'}`}
                      >
                        {cat.implication}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Analysis & Intervention Recommendation */}
      {(data.dominant4R || data.technicalStrategy || (data.recommendations && data.recommendations.length > 0)) && (
        <div className='card border-0 shadow-sm rounded-4 mt-4 overflow-hidden'>
          <div className='card-body p-4'>
            <div className='text-uppercase extra-small fw-bold text-muted letter-spacing-1 mb-3'>
              Rekomendasi Intervensi
            </div>

            {data.dominant4R && (
              <div className='mb-3'>
                <span className='badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 rounded-pill px-4 py-2 fw-bold'>
                  <i className='bi bi-bullseye me-2'></i>
                  Tahap 4R Paling Dominan: {data.dominant4R}
                </span>
              </div>
            )}

            {data.technicalStrategy && (
              <div className='bg-light rounded-4 p-3 mb-3'>
                <div className='extra-small fw-bold text-muted text-uppercase mb-1'>
                  Strategi Tindak Lanjut Teknis
                </div>
                <p
                  className='text-dark small mb-0 fw-medium'
                  style={{ lineHeight: '1.6' }}
                >
                  {data.technicalStrategy}
                </p>
              </div>
            )}

            {data.recommendations && data.recommendations.length > 0 && (
              <details>
                <summary
                  className='small fw-bold text-primary'
                  style={{ cursor: 'pointer', listStyle: 'none' }}
                >
                  <i className='bi bi-chevron-right me-1 small'></i>
                  Lihat Detail Rekomendasi ({data.recommendations.length})
                </summary>
                <div className='vstack gap-2 mt-3'>
                  {data.recommendations.map((rec, i) => (
                    <div
                      key={i}
                      className='d-flex align-items-start p-3 border rounded-3 bg-white shadow-xs'
                    >
                      <div
                        className='bg-light text-muted rounded-circle d-flex align-items-center justify-content-center me-3 small fw-bold'
                        style={{
                          width: '28px',
                          height: '28px',
                          minWidth: '28px',
                        }}
                      >
                        {(i + 1).toString().padStart(2, '0')}
                      </div>
                      <div className='text-dark small fw-medium mt-1'>
                        {rec}
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BumdesVECISummary;
